/* global SideDrawer, trapFocus, removeTrapFocus */

if (!customElements.get('quick-add-drawer')) {
    class QuickAddDrawer extends SideDrawer {
        constructor() {
            super();
            this.content = this.querySelector('.js-product-details');
            this.footer = this.querySelector('.drawer__footer');
            this.form = this.querySelector('product-form');
            this.notification = this.querySelector('.js-added-to-cart');
            this.backBtn = this.querySelector('.drawer__back-btn');
            this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
            this.cartDrawer = document.querySelector('cart-drawer');
            this.documentClickHandler = this.handleDocumentClick.bind(this);

            document.addEventListener('click', this.documentClickHandler);
            this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

            this.openCartDrawerLinks.forEach((link) => {
                link.addEventListener('click', this.handleOpenCartClick.bind(this));
            });
        }

        disconnectedCallback() {
            document.removeEventListener('click', this.documentClickHandler);
        }

        /**
         * Handles 'click' events on success notification's 'View cart' link
         * @param {object} evt - Event object.
         */
        handleOpenCartClick(evt) {
            // Open the cart drawer if available on the page
            if (this.cartDrawer) {
                evt.preventDefault();
                this.cartDrawer.open();
            } else if (window.location.pathname === theme.routes.cart) {
                evt.preventDefault();
                this.close();
            }
        }

        /**
         * Handles 'click' events on the document.
         * Also called directly by siblingLinkHandler.
         * @param {object} evt - Event object.
         */
        handleDocumentClick(evt) {
            if (!evt.target.matches('.js-quick-add')) return;

            // Handle an anchor tag
            if (evt.target.tagName === 'A') {
                evt.preventDefault();
            }

            // Close the cart drawer if it's open
            if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
                const overlay = document.querySelector('.js-overlay.is-visible');
                if (overlay) overlay.style.transitionDelay = '200ms';

                this.cartDrawer.close();

                // Wait a few ms for a more optimal ux/animation
                setTimeout(() => {
                    this.backBtn.hidden = false;
                    this.open(evt.target);
                    if (overlay) overlay.style.transitionDelay = '';
                }, 200);
            } else {
                this.open(evt.target);
            }
        }

        /**
         * Handles 'on:variant:change' events on the Quick Add drawer.
         * @param {object} evt - Event object.
         */
        handleVariantChange(evt) {
            let url = this.productUrl;

            if (evt.detail.variant) {
                const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
                url += `${separator}variant=${evt.detail.variant.id}`;
            }

            this.querySelectorAll('.js-prod-link').forEach((link) => {
                link.href = url;
            });
        }

        /**
         * Opens the drawer and fetches the product details.
         * @param {Element} opener - Element that triggered opening of the drawer.
         */
        async open(opener) {
            opener.setAttribute('aria-disabled', 'true');
            if (this.notification) this.notification.hidden = true;

            this.header = this.querySelector('.js-quick-add-heading');
            this.productUrl = opener.tagName === 'A' ? opener.href : opener.dataset.productUrl;

            this.header.innerHTML = '';
            this.content.innerHTML = '';
            this.footer.innerHTML = '';
            document.querySelector('.quick-add__header--price')?.remove();

            this.classList.add('is-loading');
            this.content.classList.add('drawer__content--out');
            this.footer.classList.add('drawer__footer--out');

            super.open(opener);

            try {
                const response = await fetch(this.productUrl);
                if (!response.ok) return;

                const pageContentHtml = await response.text();
                this.renderProduct(opener, pageContentHtml);

                if (this.querySelector('.shopify-payment-button')) {
                    setTimeout(() => {
                        removeTrapFocus();
                        trapFocus(this);
                    }, 1000);
                } else {
                    removeTrapFocus();
                    trapFocus(this);
                }
            } finally {
                opener.removeAttribute('aria-disabled');
            }
        }

        /**
         * Closes the Quick Add drawer.
         */
        close() {
            document.querySelector('.js-overlay').innerHTML = '';
            super.close(() => {
                this.backBtn.hidden = true;
            });
        }

        /**
         * Closes the Quick Add drawer as part of a handoff to the cart drawer.
         * 
         * This intentionally keeps the shared overlay/backdrop alive so the screen
         * does not flash between closing Quick Add and opening the Cart Drawer.
         * 
         * @param {Function} [callback] - Function to run after the drawer has had time to close.
         */
        closeForCartDrawer(callback) {
            super.close(() => {
                this.backBtn.hidden = true;

                if (typeof callback === 'function') {
                    callback();
                }
            });
        }

        /**
         * Renders the product details from the real product page product section.
         * @param {Element} opener - Element that triggered opening of the drawer.
         * @param {string} pageContentHtml - Product page HTML for the product we wish to show.
         */
        renderProduct(opener, pageContentHtml) {
            const tmpl = document.createElement('template');
            tmpl.innerHTML = pageContentHtml;

            const productRoot = tmpl.content.querySelector('.cc-main-product .js-product');
            if (!productRoot) return;

            this.productEl = productRoot;

            // Load any external resources included with the real product section.
            tmpl.content.querySelectorAll('.cc-main-product link[rel="stylesheet"]').forEach((el) => {
                if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
                    document.head.insertAdjacentHTML('beforeend', el.outerHTML);
                }
            });

            tmpl.content.querySelectorAll('.cc-main-product script[src]').forEach((el) => {
                if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
                    const scr = document.createElement('script');
                    scr.src = el.getAttribute('src');
                    scr.defer = true;
                    document.head.appendChild(scr);
                }
            });

            const sectionId = productRoot.dataset.section;
            const productInfo = productRoot.querySelector('[id^="product-info-template--"]');
            if (!productInfo) return;

            const clonedProductInfo = productInfo.cloneNode(true);

            // Prevent duplicate IDs and mismatched form/label targets inside the drawer.
            if (sectionId) {
                clonedProductInfo.innerHTML = clonedProductInfo.innerHTML.replaceAll(sectionId, 'quickadd');
            }

            // Prevent variant picker from updating the page URL.
            const variantPicker = clonedProductInfo.querySelector('variant-picker');
            if (variantPicker) {
                variantPicker.dataset.updateUrl = 'false';
            }

            // Remove size chart modal and trigger if present.
            const sizeChartModal = clonedProductInfo.querySelector('#size-chart');
            if (sizeChartModal) {
                clonedProductInfo.querySelector('[data-modal="size-chart"]')?.remove();
                sizeChartModal.remove();
            }

            this.updateContent(clonedProductInfo);
            this.updateForm(clonedProductInfo);

            this.runInjectedScripts(this);

            this.form = this.querySelector('product-form');

            if (this.form && typeof this.form.init === 'function') {
                this.form.init();
            }

            if (opener.dataset.selectedColor) {
                this.setActiveVariant(opener);
            }

            if (this.querySelector('.product-options__siblings a')) {
                this.querySelectorAll('.product-options__siblings a').forEach((el) => {
                    el.addEventListener('click', this.siblingLinkHandler.bind(this));
                });
            }
        }

        runInjectedScripts(container) {
    container.querySelectorAll('script[data-run-on-inject]').forEach((oldScript) => {
        const script = document.createElement('script');

        [...oldScript.attributes].forEach((attr) => {
            script.setAttribute(attr.name, attr.value);
        });

        script.textContent = oldScript.textContent;
        oldScript.replaceWith(script);
    });
}

        /**
         * Handling selecting a sibling in the product form
         * @param {object} evt - Event object.
         */
        siblingLinkHandler(evt) {
            evt.preventDefault();
            const qbBtn = document.createElement('button');
            qbBtn.className = 'js-quick-add';
            qbBtn.dataset.productUrl = evt.currentTarget.getAttribute('href');
            this.handleDocumentClick({ target: qbBtn });
        }

        /**
         * Set color variant to match the one selected in the card.
         * @param {Element} opener - Element that triggered opening of the drawer.
         */
        setActiveVariant(opener) {
            const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
            if (colorOptionBox) {
                setTimeout(() => {
                    this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)?.click();
                }, 100);
            } else {
                const colorOptionDropdown = this.querySelector(
                    `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
                );
                if (colorOptionDropdown) {
                    const customSelect = colorOptionDropdown.closest('custom-select');
                    customSelect?.selectOption(colorOptionDropdown);
                }
            }
        }

        /**
         * Builds the drawer content using the real product page media + real product-info section markup.
         * @param {Element} productInfoEl
         */
        updateContent(productInfoEl) {
            // this.header.innerHTML = theme.strings.quickAddHeading;

            const titleEl = productInfoEl.querySelector('h1.product-title');
            if (titleEl) {
                const h3 = document.createElement('h3');
                h3.className = 'product-title mb-0';
                h3.textContent = titleEl.textContent.trim();
                titleEl.replaceWith(h3);
            }

            productInfoEl.querySelectorAll('details-disclosure, .product-info__inner > .product-usps.leading-8.normal-case:last-of-type').forEach(e => e.remove());

            const productMediaHtml = this.getElementHtml('#product-media');

            const parser = new DOMParser();
            const productMedia = parser.parseFromString(productMediaHtml, 'text/html');
            productMedia.querySelector('[data-layout="stacked"]')?.setAttribute('data-layout', 'slider');

            const mediaCount = productMedia.querySelectorAll('.media-viewer__item')?.length || 0;
            const thumbs = productMedia.querySelector('.media-gallery__thumbs');
            if (thumbs) thumbs.remove();

            // Remove product-form from the content block because it belongs in the footer.
            productInfoEl.querySelector('product-form')?.remove();

            this.content.innerHTML = `
        <div class="product-media product-media--slider mt-8 mb-8${mediaCount === 1 ? ' product-media--single-slider' : ''}">
          ${productMedia.body.innerHTML}
        </div>
        <div class="quick-add-info grid mb-2">
          <div class="quick-add-info__details">
            ${productInfoEl.innerHTML}
          </div>
        </div>
                <div class="text-theme-text mt-auto items-end">
          <a href="${this.productUrl}" class="label">
            ${theme.strings.viewDetails}
            <span class="pagination__arrow-icon" aria-hidden="true" style="float: right;">
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
            </span>
          </a>
        </div>
      `;

            this.classList.remove('is-loading');
            this.content.classList.remove('drawer__content--out');
        }

        /**
         * Updates the Quick Add drawer footer with the real product page product form.
         * @param {Element} productInfoEl
         */
        updateForm(productInfoEl) {
            const productForm = this.productEl.querySelector('product-form');
            this.footer.classList.remove('quick-add__footer-message');

            if (!this.footer) return;

            if (productForm) {
                this.footer.innerHTML = '';
                this.footer.appendChild(productForm.cloneNode(true));
            } else {
                this.footer.innerHTML = '';
            }

            this.footer.classList.remove('drawer__footer--out');
        }

        /**
         * Gets the innerHTML of elements within the product element.
         * @param {string} selector - CSS selector for the element.
         * @param {...*} additionalSelectors - Additional CSS selectors for elements.
         * @returns {?string}
         */
        getElementHtml(selector, ...additionalSelectors) {
            const selectors = [selector].concat(additionalSelectors);
            let html = '';

            selectors.forEach((sel) => {
                const els = this.productEl.querySelectorAll(sel);
                Array.from(els).forEach((el) => {
                    html += el.innerHTML;
                });
            });

            return html;
        }

        /**
         * Shows an "Added to cart" message in the drawer.
         */
        addedToCart() {
            if (this.notification) {
                setTimeout(() => {
                    this.notification.hidden = false;
                }, 300);

                setTimeout(() => {
                    this.notification.hidden = true;
                }, this.notification.dataset.visibleFor);
            }
        }
    }

    customElements.define('quick-add-drawer', QuickAddDrawer);
}